const CONTROL_EXPLANATIONS = {
  // ── ACCESS CONTROL ──────────────────────────────────────────────────────────
  'AC.L2-3.1.1': {
    plain_english: 'Only authorized users, processes, and devices should be allowed to access your systems and the CUI stored on them. You must be able to identify who or what is accessing your systems and ensure unauthorized users cannot get in.',
    example: 'A small defense contractor sets up Windows Active Directory with user accounts for each employee. Only employees with a valid login can access the file server where contract documents are stored.',
    evidence_tips: [
      'Screenshot of Active Directory showing user accounts and group memberships',
      'Access control policy document listing who is authorized to access CUI systems',
      'User account listing from your IT system with roles assigned',
    ],
    common_mistakes: 'Sharing a single login account among multiple employees, making it impossible to know who accessed what.',
    nist_reference: '3.1.1',
  },
  'AC.L2-3.1.2': {
    plain_english: 'Each authorized user should only be able to access the specific systems, files, and functions they need to do their job — no more. This is called least privilege.',
    example: 'An engineer gets access to the CAD file share but not the HR folder. The accounting team can access QuickBooks but not the engineering design server.',
    evidence_tips: [
      'File share permission report showing user/group access levels per folder',
      'Role-based access matrix document mapping job titles to system permissions',
      'Screenshot of Active Directory group policy showing restricted access settings',
    ],
    common_mistakes: 'Giving everyone Domain Admin or full read-write access to all folders because it is easier to manage.',
    nist_reference: '3.1.2',
  },
  'AC.L2-3.1.3': {
    plain_english: 'Control the flow of CUI within your organization and to outside parties. CUI should only move through approved, secure paths — not emailed to personal accounts or saved to unencrypted USB drives.',
    example: 'Company policy prohibits emailing contract drawings to personal Gmail accounts. CUI must be sent via encrypted email or a secure file-sharing portal approved by the company.',
    evidence_tips: [
      'Data flow diagram showing how CUI moves between systems and to external parties',
      'Acceptable Use Policy prohibiting CUI transfer to unauthorized systems',
      'Email DLP configuration screenshot or policy document',
    ],
    common_mistakes: 'Employees emailing CUI to personal accounts to work from home without any controls in place.',
    nist_reference: '3.1.3',
  },
  'AC.L2-3.1.4': {
    plain_english: 'Separate duties so no single person can misuse your systems without being detected. Critical actions should require two different people.',
    example: 'The person who creates a new vendor account in your accounting system cannot also approve payments to that vendor. Two separate employees handle each step.',
    evidence_tips: [
      'Separation of duties matrix showing which roles cannot be held by the same person',
      'Workflow documentation showing approval steps that require multiple sign-offs',
      'System configuration screenshot showing dual-approval workflow',
    ],
    common_mistakes: 'One administrator having the ability to create accounts, approve changes, and delete audit logs with no oversight.',
    nist_reference: '3.1.4',
  },
  'AC.L2-3.1.5': {
    plain_english: 'Use the principle of least privilege — accounts should have only the minimum access required. This applies especially to privileged accounts like system administrators.',
    example: 'Your IT admin has a separate admin account for IT tasks and a regular user account for daily work like email. The admin account is only used when performing IT administration tasks.',
    evidence_tips: [
      'Account listing showing separate admin and standard user accounts for IT staff',
      'Policy document requiring least privilege and prohibiting using admin accounts for daily tasks',
      'Periodic admin account privilege review records',
    ],
    common_mistakes: 'IT staff logging in with admin accounts every day for routine tasks like checking email, unnecessarily expanding attack surface.',
    nist_reference: '3.1.5',
  },
  'AC.L2-3.1.6': {
    plain_english: 'Use non-privileged accounts for everyday activities. Only use privileged accounts when doing tasks that require elevated permissions.',
    example: 'Your sysadmin has two accounts: jsmith for regular work and jsmith-admin for installing software or managing servers. They only use the admin account when specifically doing IT work.',
    evidence_tips: [
      'User account policy requiring separate accounts for privileged and non-privileged tasks',
      'Screenshot of user accounts list showing admin accounts designated separately',
      'Training record showing IT staff received instruction on proper account usage',
    ],
    common_mistakes: 'Using a single account with admin rights for all daily work, browsing the internet, and doing IT administration.',
    nist_reference: '3.1.6',
  },
  'AC.L2-3.1.7': {
    plain_english: 'Prevent users from giving themselves or others more privileges than they are supposed to have. Users should not be able to escalate their own permissions.',
    example: 'Standard users cannot add themselves to the Administrators group in Active Directory. Only the IT admin account can change group memberships.',
    evidence_tips: [
      'Active Directory screenshot showing standard users cannot modify group memberships',
      'Group Policy configuration restricting privilege escalation',
      'Access control policy prohibiting self-granting of elevated privileges',
    ],
    common_mistakes: 'Local administrator rights given to all workstations allow any user to install software and modify system settings.',
    nist_reference: '3.1.7',
  },
  'AC.L2-3.1.8': {
    plain_english: 'Unsuccessful login attempts should trigger an account lockout to prevent attackers from guessing passwords. Define a threshold and what happens when it is reached.',
    example: 'After 5 failed login attempts within 15 minutes, the account is locked for 30 minutes. The IT help desk must manually unlock accounts after verifying identity.',
    evidence_tips: [
      'Group Policy screenshot showing account lockout threshold and duration settings',
      'Authentication system configuration showing lockout parameters',
      'Written policy defining the lockout threshold and unlock procedure',
    ],
    common_mistakes: 'No account lockout policy configured, allowing unlimited login attempts and making brute force attacks trivial.',
    nist_reference: '3.1.8',
  },
  'AC.L2-3.1.9': {
    plain_english: 'Display a privacy or security notice when users log into your systems, making them acknowledge acceptable use before accessing CUI.',
    example: 'When logging into the company VPN or file server, a banner appears: "This system contains CUI. Unauthorized access is prohibited. By continuing you consent to monitoring."',
    evidence_tips: [
      'Screenshot of login banner displayed on key systems (VPN, file server, workstation)',
      'System configuration showing the banner text and settings',
      'Acceptable Use Policy referenced by the banner',
    ],
    common_mistakes: 'No login banners configured on any systems, so users have no notification about the system\'s sensitive nature.',
    nist_reference: '3.1.9',
  },
  'AC.L2-3.1.10': {
    plain_english: 'Automatically lock sessions after a period of inactivity. Users should not be able to leave computers unattended with access to CUI systems.',
    example: 'Workstations automatically lock with a password-protected screensaver after 15 minutes of inactivity. Users must re-enter their password to resume.',
    evidence_tips: [
      'Group Policy screenshot showing screen lock timeout setting (15 minutes or less)',
      'System screenshot showing screensaver lock configuration',
      'Policy document defining inactivity timeout requirements',
    ],
    common_mistakes: 'No screen lock configured, leaving CUI accessible on unattended workstations throughout the office.',
    nist_reference: '3.1.10',
  },
  'AC.L2-3.1.11': {
    plain_english: 'Terminate sessions after a defined period of inactivity. The actual session ends and users must fully log back in — stronger than just locking the screen.',
    example: 'The web-based contract portal automatically logs users out after 30 minutes of inactivity, requiring full re-authentication to continue.',
    evidence_tips: [
      'Application or VPN session timeout configuration screenshot',
      'Policy document specifying session termination timeouts',
      'Network configuration showing idle session disconnect settings',
    ],
    common_mistakes: 'Sessions never time out on critical systems, leaving open connections that could be hijacked.',
    nist_reference: '3.1.11',
  },
  'AC.L2-3.1.12': {
    plain_english: 'Monitor and control remote access sessions to your systems. Remote connections must be managed and only through approved methods.',
    example: 'Remote employees connect via a company-managed VPN using multi-factor authentication. All remote sessions are logged. Direct RDP to internal servers from outside the network is blocked.',
    evidence_tips: [
      'VPN configuration showing remote access requires MFA and logs sessions',
      'Firewall rules showing direct remote desktop is blocked from the internet',
      'Remote Access Policy document defining authorized remote connection methods',
    ],
    common_mistakes: 'RDP port 3389 is open to the internet without MFA, exposing systems to brute force attacks.',
    nist_reference: '3.1.12',
  },
  'AC.L2-3.1.13': {
    plain_english: 'Encrypt remote access sessions to protect CUI from being intercepted during transmission.',
    example: 'All VPN connections use AES-256 encryption. Remote desktop sessions are tunneled through the encrypted VPN. Plain HTTP connections are not used for systems with CUI.',
    evidence_tips: [
      'VPN configuration showing encryption protocol (AES-256, IKEv2)',
      'SSL/TLS certificate and configuration for web-based CUI portals',
      'Remote access policy specifying encrypted connection requirements',
    ],
    common_mistakes: 'Using unencrypted Telnet or plain HTTP for internal remote management tools.',
    nist_reference: '3.1.13',
  },
  'AC.L2-3.1.14': {
    plain_english: 'Route all remote access through controlled access points, not directly to individual systems. Traffic should flow through a managed gateway.',
    example: 'All remote users connect to a VPN concentrator first, then access internal resources through it. Users cannot connect directly to individual servers without going through the VPN.',
    evidence_tips: [
      'Network diagram showing VPN as the single entry point for remote users',
      'Firewall rules showing inbound connections are only allowed through the VPN',
      'Remote access policy requiring use of the approved VPN gateway',
    ],
    common_mistakes: 'Individual servers have their own VPN or remote access software configured independently, bypassing centralized controls.',
    nist_reference: '3.1.14',
  },
  'AC.L2-3.1.15': {
    plain_english: 'Authorize remote execution of privileged commands only through secure, approved channels. Privileged actions taken remotely need extra scrutiny.',
    example: 'IT admins can only run elevated commands remotely through the company VPN using their dedicated admin accounts, not from personal devices or networks.',
    evidence_tips: [
      'Policy prohibiting privileged remote administration except through approved channels',
      'VPN logs showing admin sessions are identified and logged',
      'Privileged access management configuration if applicable',
    ],
    common_mistakes: 'IT staff remotely administering systems from personal home computers with no controls or logging.',
    nist_reference: '3.1.15',
  },
  'AC.L2-3.1.16': {
    plain_english: 'Before allowing wireless access, authorize the connection and ensure it meets your security requirements.',
    example: 'The company WiFi for business devices requires WPA3-Enterprise with certificate-based authentication. No devices can connect without IT approval.',
    evidence_tips: [
      'Wireless network configuration showing authentication requirements',
      'Device management policy requiring IT approval before wireless connection',
      'Network access control configuration if applicable',
    ],
    common_mistakes: 'Company WiFi uses WPA2 with a shared password that never changes and is shared with visitors.',
    nist_reference: '3.1.16',
  },
  'AC.L2-3.1.17': {
    plain_english: 'Protect wireless access using authentication and encryption. Wireless signals can be intercepted so they need strong protection.',
    example: 'Business WiFi uses WPA2-Enterprise or WPA3-Enterprise with 802.1X authentication. A separate guest WiFi network is isolated from systems containing CUI.',
    evidence_tips: [
      'WiFi configuration showing WPA2/WPA3-Enterprise encryption',
      'Network segmentation diagram showing CUI network is separate from guest WiFi',
      'Policy requiring only company-approved WiFi for accessing CUI',
    ],
    common_mistakes: 'Using WPA2-Personal with a weak shared password for business WiFi, making it easy for nearby attackers to join the network.',
    nist_reference: '3.1.17',
  },
  'AC.L2-3.1.18': {
    plain_english: 'Control what devices and systems can connect to your network and what they can access. Not every device that connects should get full access.',
    example: 'Mobile device management (MDM) is deployed so only company-enrolled phones can access company email. Personal phones can only access guest WiFi.',
    evidence_tips: [
      'MDM configuration showing company device enrollment requirements',
      'Network access control policy for mobile and personal devices',
      'Certificate-based enrollment settings for wireless access',
    ],
    common_mistakes: 'Any device can connect to company WiFi with the shared password and immediately access internal file shares.',
    nist_reference: '3.1.18',
  },
  'AC.L2-3.1.19': {
    plain_english: 'Encrypt CUI stored on mobile devices and laptops, especially when they leave your facility. Lost or stolen devices should not expose CUI.',
    example: 'All company laptops have BitLocker enabled with a PIN. If a laptop is lost, the data is unreadable without the encryption key.',
    evidence_tips: [
      'BitLocker or FileVault configuration screenshot showing encryption is enabled on all laptops',
      'MDM policy enforcing encryption on mobile devices',
      'Asset inventory showing encryption status for all portable devices',
    ],
    common_mistakes: 'Laptops with CUI have no encryption — a lost laptop means a reportable data breach.',
    nist_reference: '3.1.19',
  },
  'AC.L2-3.1.20': {
    plain_english: 'Control and monitor the use of external connections like personal VPNs or proxies that could bypass your security controls.',
    example: 'Company policy prohibits employees from using personal VPNs on company computers. IT monitors outbound traffic for unauthorized tunneling software.',
    evidence_tips: [
      'Acceptable Use Policy prohibiting unauthorized external connections',
      'Firewall rules blocking common VPN ports to prevent unauthorized tunneling',
      'Network monitoring logs showing detection of unauthorized connection attempts',
    ],
    common_mistakes: 'Employees install personal VPN software on company laptops to bypass web filtering, creating unmonitored exit points.',
    nist_reference: '3.1.20',
  },
  'AC.L2-3.1.21': {
    plain_english: 'Limit the use of portable storage devices on systems that contain CUI. Unauthorized USB use is a major data exfiltration risk.',
    example: 'Group Policy blocks all USB storage device access on workstations by default. Only IT-approved, encrypted USB drives can be used by staff who request an exception.',
    evidence_tips: [
      'Group Policy screenshot showing USB storage restriction settings',
      'Written policy on USB device usage and the approval process for exceptions',
      'Endpoint security configuration blocking unauthorized removable media',
    ],
    common_mistakes: 'No USB restrictions in place — any employee can plug in a personal USB drive and copy CUI with no logging.',
    nist_reference: '3.1.21',
  },
  'AC.L2-3.1.22': {
    plain_english: 'Control CUI posted to or accessed from publicly accessible systems. Information on public websites or portals must be reviewed to ensure CUI is not inadvertently disclosed.',
    example: 'Before publishing project updates on the company website, a designated reviewer checks that no CUI, contract numbers, or sensitive specifications are included.',
    evidence_tips: [
      'Review checklist used before publishing content to the company website',
      'Policy defining what information can and cannot be posted publicly',
      'List of approved individuals who can publish to public-facing systems',
    ],
    common_mistakes: 'Employees post project descriptions on LinkedIn that include contract numbers, technical specifications, or details about defense work.',
    nist_reference: '3.1.22',
  },
  // ── AWARENESS AND TRAINING ──────────────────────────────────────────────────
  'AT.L2-3.2.1': {
    plain_english: 'Ensure all personnel who touch CUI systems understand their security responsibilities before they have access, and receive regular refreshers.',
    example: 'New employees complete a cybersecurity awareness training course during onboarding. All employees complete an annual refresher covering CMMC requirements and CUI handling.',
    evidence_tips: [
      'Training completion records showing all personnel completed security awareness training',
      'Training curriculum covering CMMC and CUI handling topics',
      'Signed acknowledgment forms confirming employees read and understood the training',
    ],
    common_mistakes: 'Only IT staff receive security training; employees who handle CUI but are not in IT roles receive no training.',
    nist_reference: '3.2.1',
  },
  'AT.L2-3.2.2': {
    plain_english: 'Train personnel to recognize and report insider threats — situations where an authorized user intentionally or accidentally misuses their access.',
    example: 'Annual training includes a module on insider threat indicators, what to do if you suspect a coworker is mishandling CUI, and how to report concerns to management.',
    evidence_tips: [
      'Training records showing insider threat awareness module completion',
      'Training materials covering insider threat warning signs and reporting procedures',
      'Written policy defining the reporting process for suspected insider threats',
    ],
    common_mistakes: 'Security training covers only external hacker threats with no mention of insider risks from current or departing employees.',
    nist_reference: '3.2.2',
  },
  'AT.L2-3.2.3': {
    plain_english: 'Provide training specific to each person\'s security role. People with special security duties need role-specific training beyond general awareness.',
    example: 'IT administrators receive advanced training on secure configuration and patch management. The CMMC program manager receives training on assessment methodologies.',
    evidence_tips: [
      'Role-specific training plans for IT staff, security officers, and CUI handlers',
      'Training completion certificates for role-specific courses',
      'Job descriptions showing security responsibilities matched to training requirements',
    ],
    common_mistakes: 'All employees including system administrators receive only the same generic phishing awareness training with no role-specific technical content.',
    nist_reference: '3.2.3',
  },
  // ── AUDIT AND ACCOUNTABILITY ────────────────────────────────────────────────
  'AU.L2-3.3.1': {
    plain_english: 'Log user activity on systems that contain CUI. You need a record of who did what, when, and from where so you can investigate incidents.',
    example: 'Windows Security Event Logging is enabled to capture logins, logouts, file accesses, and permission changes on the file server containing contract documents.',
    evidence_tips: [
      'Windows Event Log policy configuration showing audit categories enabled',
      'Log management system screenshot showing active log collection',
      'Sample audit log showing user login events with timestamps',
    ],
    common_mistakes: 'Windows audit logging is not enabled by default and organizations never configure it, leaving no record of who accessed what.',
    nist_reference: '3.3.1',
  },
  'AU.L2-3.3.2': {
    plain_english: 'Make sure you can trace actions back to individual users. Shared accounts make this impossible — each person needs their own account.',
    example: 'Every employee has a unique user account. Shared or generic accounts like lab-user are prohibited. Audit logs show which individual employee performed each action.',
    evidence_tips: [
      'User account policy prohibiting shared accounts',
      'Active Directory user account list showing one account per employee',
      'Sample audit log entry showing a specific named user and their actions',
    ],
    common_mistakes: 'Multiple technicians share a single login for a test system, making it impossible to determine which individual made a change.',
    nist_reference: '3.3.2',
  },
  'AU.L2-3.3.3': {
    plain_english: 'Review audit logs regularly and investigate unusual activity. Logs that are never reviewed provide no security value.',
    example: 'The IT manager reviews security event logs weekly. An automated alert sends an email when more than 10 failed login attempts occur for a single account within an hour.',
    evidence_tips: [
      'Log review procedure document showing how often logs are reviewed',
      'SIEM or log management system showing configured alerts',
      'Log review records or tickets showing findings were documented',
    ],
    common_mistakes: 'Audit logs are enabled and stored but never reviewed — an intrusion occurring over weeks goes undetected.',
    nist_reference: '3.3.3',
  },
  'AU.L2-3.3.4': {
    plain_english: 'Alert the right people immediately when audit logging fails. If your logging system goes down, you lose your security visibility.',
    example: 'The SIEM system sends an automated email alert to the IT admin if log collection stops from any monitored system within 5 minutes of the failure.',
    evidence_tips: [
      'SIEM alert configuration showing log collection failure alerts',
      'Email notification settings for audit system failures',
      'Incident response procedure for audit log failures',
    ],
    common_mistakes: 'The logging server runs out of disk space and stops collecting logs — no one notices for weeks because there are no alerts configured.',
    nist_reference: '3.3.4',
  },
  'AU.L2-3.3.5': {
    plain_english: 'Correlate audit records across different systems to get a complete picture of events. Reviewing logs from individual systems in isolation misses patterns.',
    example: 'The SIEM ingests logs from the firewall, Active Directory, file server, and VPN. When an unusual login is followed by a large file download, the SIEM correlates these events and creates an alert.',
    evidence_tips: [
      'SIEM configuration showing multiple log sources feeding into a single correlation platform',
      'Correlation rule examples showing how events from different sources are linked',
      'Network topology diagram showing which systems feed logs to the SIEM',
    ],
    common_mistakes: 'Logs from different systems are stored separately and never correlated, missing multi-step attack patterns.',
    nist_reference: '3.3.5',
  },
  'AU.L2-3.3.6': {
    plain_english: 'Reduce the audit information to what is actually important and useful. Use tools to filter and focus on security-relevant events.',
    example: 'The SIEM filters out routine system events and only alerts on high-priority events like privilege escalation, after-hours logins, and bulk file access.',
    evidence_tips: [
      'SIEM filter and alert configuration showing relevant events are prioritized',
      'Log review reports showing summarized findings',
      'Documentation of the log review process and event triage',
    ],
    common_mistakes: 'Reviewers are overwhelmed with thousands of routine log entries and cannot identify the few security-relevant events buried within.',
    nist_reference: '3.3.6',
  },
  'AU.L2-3.3.7': {
    plain_english: 'Ensure system clocks are synchronized so timestamps in audit logs are accurate and consistent across systems. Inconsistent timestamps make investigating incidents nearly impossible.',
    example: 'All servers and workstations sync their time to an internal NTP server which syncs to NIST time servers. All log timestamps are accurate within 1 second.',
    evidence_tips: [
      'NTP configuration on servers and workstations showing time source',
      'Screenshot of time sync status showing clocks are synchronized',
      'Network policy requiring NTP sync for all devices',
    ],
    common_mistakes: 'Different systems have clocks that differ by hours, making it impossible to correlate events across systems during an investigation.',
    nist_reference: '3.3.7',
  },
  'AU.L2-3.3.8': {
    plain_english: 'Protect audit logs from unauthorized access, modification, or deletion. If attackers can delete logs, they can hide their tracks.',
    example: 'Audit logs are stored on a separate, dedicated log server that only the IT admin can access. Regular users and even server admins cannot delete or modify logs on the log server.',
    evidence_tips: [
      'Access controls on the log server showing only authorized admins can access it',
      'SIEM or log management system configured with tamper-evident storage',
      'Policy requiring logs to be stored separately from production systems',
    ],
    common_mistakes: 'Logs are stored on the same server being monitored, allowing an attacker who gains admin access to delete evidence of their intrusion.',
    nist_reference: '3.3.8',
  },
  'AU.L2-3.3.9': {
    plain_english: 'Limit who can manage (enable, disable, configure) audit logging. Only authorized personnel should be able to change logging settings.',
    example: 'Only the IT Security Manager can modify audit policy settings in Group Policy. Regular IT staff can view but not change audit configurations.',
    evidence_tips: [
      'Access control list showing who can modify audit policy',
      'Change management records for any modifications to audit settings',
      'Role-based access configuration for the audit management system',
    ],
    common_mistakes: 'Any domain admin can disable auditing with no review of who changes logging configurations.',
    nist_reference: '3.3.9',
  },
  // ── CONFIGURATION MANAGEMENT ────────────────────────────────────────────────
  'CM.L2-3.4.1': {
    plain_english: 'Create and maintain a baseline configuration for all systems — a documented standard for how each system should be set up securely.',
    example: 'IT creates a hardening checklist for Windows workstations based on CIS benchmarks, documenting required settings for each type of system in your environment.',
    evidence_tips: [
      'System baseline configuration document for each major system type (workstations, servers)',
      'CIS benchmark or STIG implementation checklist',
      'Configuration management tool showing baseline applied to systems',
    ],
    common_mistakes: 'Systems are configured ad hoc with no documented standard, making it impossible to verify all systems meet security requirements.',
    nist_reference: '3.4.1',
  },
  'CM.L2-3.4.2': {
    plain_english: 'Establish and document a process for managing changes to your systems. Unapproved changes can introduce vulnerabilities.',
    example: 'All changes to servers require a change request ticket approved by the IT manager before implementation. Emergency changes must be documented within 24 hours.',
    evidence_tips: [
      'Change management policy and procedure document',
      'Sample change request tickets showing the approval process',
      'Change log showing approved and completed changes with dates',
    ],
    common_mistakes: 'Admins make configuration changes on the fly without documentation or approval, leaving no record of what changed and when.',
    nist_reference: '3.4.2',
  },
  'CM.L2-3.4.3': {
    plain_english: 'Track and review changes to your systems. Analyzing the impact of changes before they happen prevents security regressions.',
    example: 'Before deploying a software update, IT reviews what the update changes and tests it in a non-production environment. Security impact is documented in the change ticket.',
    evidence_tips: [
      'Change impact assessment templates used before implementing changes',
      'Test environment documentation showing changes are tested before production',
      'Change approval records showing security review step',
    ],
    common_mistakes: 'Updates are pushed to production immediately with no testing, occasionally breaking security settings.',
    nist_reference: '3.4.3',
  },
  'CM.L2-3.4.4': {
    plain_english: 'Analyze the security impact of changes before implementing them. A new software installation or configuration change could introduce vulnerabilities.',
    example: 'When a vendor requests installing new software on a system with CUI, the IT security lead reviews the software, checks for known vulnerabilities, and documents the security assessment.',
    evidence_tips: [
      'Security review checklist for evaluating new software or configuration changes',
      'Vulnerability scan results run on new software before deployment',
      'Documentation showing security impact was assessed for recent changes',
    ],
    common_mistakes: 'New software requested by employees is installed without any security review, potentially introducing backdoors or vulnerable components.',
    nist_reference: '3.4.4',
  },
  'CM.L2-3.4.5': {
    plain_english: 'Define, document, and enforce security configuration settings for your IT products. Use industry security benchmarks as your starting point.',
    example: 'All workstations are configured according to the CIS Microsoft Windows 11 Benchmark. Settings are enforced through Group Policy and verified quarterly.',
    evidence_tips: [
      'Documented security configuration standards for each system type',
      'Group Policy Object (GPO) configuration report',
      'Compliance scan results showing systems meet configuration baseline',
    ],
    common_mistakes: 'Systems run with default out-of-the-box configurations which typically have many security settings disabled for ease of use.',
    nist_reference: '3.4.5',
  },
  'CM.L2-3.4.6': {
    plain_english: 'Implement the principle of least functionality — systems should only run the software and services they need for their purpose. Disable or remove everything else.',
    example: 'The file server runs only file sharing services. IIS, Telnet, FTP, and other unnecessary services are disabled. The workstation image does not include games or media player software.',
    evidence_tips: [
      'Service configuration showing unnecessary services are disabled',
      'Software inventory showing only approved applications are installed',
      'Application whitelist or approved software list',
    ],
    common_mistakes: 'Servers run numerous unused services from default installations (IIS, FTP, Telnet), each one a potential attack vector.',
    nist_reference: '3.4.6',
  },
  'CM.L2-3.4.7': {
    plain_english: 'Restrict, disable, or prevent the use of programs, functions, ports, protocols, and services that are not required. If you do not need it, block it.',
    example: 'Firewall rules block all inbound ports except those explicitly needed. Outbound connections to known malicious domains are blocked at the DNS and firewall level.',
    evidence_tips: [
      'Firewall rule set showing deny-by-default with only required ports/protocols allowed',
      'Application control policy listing permitted and blocked applications',
      'Network scan results showing only expected ports are open',
    ],
    common_mistakes: 'Firewalls are configured with broad allow-any rules that permit all outbound traffic and many unnecessary inbound services.',
    nist_reference: '3.4.7',
  },
  'CM.L2-3.4.8': {
    plain_english: 'Apply deny-by-default and allow-by-exception policies. Only explicitly approved software should be allowed to run on your systems.',
    example: 'Application whitelisting software is deployed. Only applications on the approved list can run. Any unknown executable triggers an alert to IT.',
    evidence_tips: [
      'Application whitelist configuration showing approved software list',
      'Policy requiring IT approval before installing any new software',
      'Endpoint security solution configured with application control',
    ],
    common_mistakes: 'Any user can download and install software from the internet without IT approval or review.',
    nist_reference: '3.4.8',
  },
  'CM.L2-3.4.9': {
    plain_english: 'Control and monitor any user-installed software. Even if users can install some software, there must be oversight.',
    example: 'Employees may install pre-approved productivity tools from an internal software catalog. Any software outside the catalog requires an IT request and security review.',
    evidence_tips: [
      'Software inventory reports showing installed applications per device',
      'Policy defining the software installation approval process',
      'Monitoring alerts triggered when unapproved software is detected',
    ],
    common_mistakes: 'Employees have local admin rights and install whatever software they want with no IT visibility.',
    nist_reference: '3.4.9',
  },
  // ── IDENTIFICATION AND AUTHENTICATION ───────────────────────────────────────
  'IA.L2-3.5.1': {
    plain_english: 'Identify all users, processes, and devices that access your systems. Each must have a unique identity so you know exactly who or what is doing what.',
    example: 'Every employee has a unique Active Directory account. Every server has a unique hostname and machine account. No generic or shared accounts are used.',
    evidence_tips: [
      'Active Directory user account list showing unique accounts per person',
      'Device inventory showing unique hostnames for all systems',
      'Policy prohibiting shared or generic accounts',
    ],
    common_mistakes: 'Multiple employees share a single office account because it is convenient, making individual accountability impossible.',
    nist_reference: '3.5.1',
  },
  'IA.L2-3.5.2': {
    plain_english: 'Verify that users, processes, and devices are who they claim to be before granting access. Passwords alone are the minimum — multi-factor authentication is strongly recommended.',
    example: 'Employees authenticate with a username, password, and a one-time code from an authenticator app. The VPN requires both certificate-based and password authentication.',
    evidence_tips: [
      'MFA configuration screenshot showing it is enabled for all users',
      'Authentication policy documenting how each system verifies user identity',
      'MFA enrollment records showing all active users are enrolled',
    ],
    common_mistakes: 'Systems rely solely on passwords with no MFA, making a single stolen password enough to compromise an account.',
    nist_reference: '3.5.2',
  },
  'IA.L2-3.5.3': {
    plain_english: 'Use multi-factor authentication for local access to privileged accounts and all network access to systems with CUI.',
    example: 'Administrators must use MFA to log into servers, even from inside the office network. Remote workers must use MFA for VPN access.',
    evidence_tips: [
      'MFA policy document specifying which accounts and systems require MFA',
      'MFA configuration showing privileged accounts are enrolled',
      'VPN and remote access configuration showing MFA is required',
    ],
    common_mistakes: 'MFA is only required for remote access but not for local logins, leaving on-site access protected only by passwords.',
    nist_reference: '3.5.3',
  },
  'IA.L2-3.5.4': {
    plain_english: 'Employ replay-resistant authentication methods. Authentication mechanisms must not be vulnerable to replay attacks where a captured credential can be reused.',
    example: 'The company uses Kerberos with session tickets or HTTPS with TLS 1.2+. Older protocols like NTLMv1 and basic HTTP authentication are disabled.',
    evidence_tips: [
      'Domain controller configuration disabling LM and NTLMv1 authentication',
      'Authentication policy requiring modern, replay-resistant protocols',
      'Network scan showing no systems use outdated authentication protocols',
    ],
    common_mistakes: 'Legacy applications use basic HTTP authentication or NTLMv1, which is vulnerable to credential replay attacks.',
    nist_reference: '3.5.4',
  },
  'IA.L2-3.5.5': {
    plain_english: 'Set a policy for how long authenticated identifiers and credentials are valid. Identifiers should not remain active indefinitely.',
    example: 'User account passwords expire every 90 days. Authentication tokens for web applications expire after 8 hours. Service account credentials are reviewed and rotated annually.',
    evidence_tips: [
      'Password policy configuration showing expiration settings',
      'Application session token expiration settings',
      'Service account management policy with rotation schedule',
    ],
    common_mistakes: 'Passwords are set to never expire, and old service account credentials from former employees remain active indefinitely.',
    nist_reference: '3.5.5',
  },
  'IA.L2-3.5.6': {
    plain_english: 'Manage credentials throughout their full lifecycle — creation, use, change, and deletion. Include service accounts, not just user accounts.',
    example: 'When an employee leaves, their account is disabled within 4 hours. Quarterly reviews identify and remove dormant accounts. Service accounts are tied to roles, not individuals.',
    evidence_tips: [
      'Offboarding checklist showing account deprovisioning as a required step',
      'Quarterly user account audit records showing dormant accounts reviewed',
      'Service account inventory with designated owners and review dates',
    ],
    common_mistakes: 'Former employees\' accounts remain active for months after departure, creating unauthorized access risks.',
    nist_reference: '3.5.6',
  },
  'IA.L2-3.5.7': {
    plain_english: 'Enforce minimum password complexity and changes. Passwords must be hard to guess, and newly set passwords cannot be the same as recent previous passwords.',
    example: 'Passwords must be at least 12 characters and include uppercase, lowercase, numbers, and symbols. Users cannot reuse their last 10 passwords.',
    evidence_tips: [
      'Password policy configuration showing complexity and history requirements',
      'Group Policy screenshot showing password settings',
      'Written password policy document distributed to employees',
    ],
    common_mistakes: 'Password policy requires only 6 characters with no complexity rules, allowing easily guessed passwords.',
    nist_reference: '3.5.7',
  },
  'IA.L2-3.5.8': {
    plain_english: 'Prohibit reusing the same password again within a defined recent history. Users should not be able to immediately cycle back to a previous password.',
    example: 'The Active Directory password policy prevents reusing the last 24 passwords. The system tracks password history and rejects any password that matches recent ones.',
    evidence_tips: [
      'Active Directory password policy showing password history count',
      'Group Policy Object report showing history enforcement',
      'Written policy defining the password reuse prohibition',
    ],
    common_mistakes: 'Password history is set to only 3, allowing users to change their password twice and immediately reuse their original one.',
    nist_reference: '3.5.8',
  },
  'IA.L2-3.5.9': {
    plain_english: 'Allow use of temporary passwords for initial authentication, but require users to change them immediately upon first use.',
    example: 'New employees receive a temporary password in a sealed envelope from HR. The system forces a password change at first login before any other actions can be taken.',
    evidence_tips: [
      'System configuration showing "must change password at next login" is forced for new accounts',
      'Onboarding procedure documenting how temporary credentials are created and delivered',
      'Help desk procedure for password resets enforcing immediate change',
    ],
    common_mistakes: 'Temporary passwords are sent via email in plaintext and never force-expired, remaining valid indefinitely.',
    nist_reference: '3.5.9',
  },
  'IA.L2-3.5.10': {
    plain_english: 'Store and transmit only cryptographically protected (hashed and salted) passwords. Never store or transmit passwords in plaintext.',
    example: 'Active Directory stores passwords as NTLM and Kerberos hashes, never in plaintext. Web applications use bcrypt to hash passwords before storage.',
    evidence_tips: [
      'Documentation confirming authentication systems use cryptographic password storage',
      'Application architecture review confirming no plaintext password storage',
      'Security configuration review of any internally-developed applications',
    ],
    common_mistakes: 'An internal legacy application stores passwords in a plain-text database field, meaning a database dump immediately exposes all user passwords.',
    nist_reference: '3.5.10',
  },
  'IA.L2-3.5.11': {
    plain_english: 'Obscure feedback when entering passwords. Password characters should be masked so shoulder surfers cannot see what is being typed.',
    example: 'All login forms show asterisks in place of password characters as they are typed. There is no unmasked show-password feature on production authentication systems.',
    evidence_tips: [
      'Screenshot of login forms showing password masking is implemented',
      'Security review of internally-developed web applications',
      'Configuration settings confirming password masking on authentication systems',
    ],
    common_mistakes: 'A custom-built internal application shows password characters in clear text, making them visible to observers.',
    nist_reference: '3.5.11',
  },
  // ── INCIDENT RESPONSE ───────────────────────────────────────────────────────
  'IR.L2-3.6.1': {
    plain_english: 'Create and implement an incident response capability. You need a documented plan for what to do when a security incident occurs.',
    example: 'The company has a written Incident Response Plan that defines what constitutes an incident, who is on the response team, escalation procedures, and how to contain and recover from incidents.',
    evidence_tips: [
      'Incident Response Plan document with current date and version',
      'Incident response team roster showing designated roles',
      'Contact list for incident response team members',
    ],
    common_mistakes: 'There is no written incident response plan, so when an incident occurs, response is ad hoc and critical steps like preserving evidence are missed.',
    nist_reference: '3.6.1',
  },
  'IR.L2-3.6.2': {
    plain_english: 'Track, document, and report security incidents. You must keep records of incidents and know when and how to report them to authorities or your contracting officer.',
    example: 'Incidents are logged in a ticket system with full documentation of the timeline, systems affected, data potentially exposed, and response actions. DoD contractors report certain incidents to DC3 within 72 hours.',
    evidence_tips: [
      'Incident log or ticketing system showing documented security incidents',
      'Incident report template used to document incidents consistently',
      'Evidence that required incidents were reported to appropriate authorities',
    ],
    common_mistakes: 'Security incidents are handled informally with no documentation, making it impossible to report to the contracting officer or identify recurring problems.',
    nist_reference: '3.6.2',
  },
  'IR.L2-3.6.3': {
    plain_english: 'Test your incident response capability regularly. Tabletop exercises and drills ensure your team knows what to do when a real incident occurs.',
    example: 'The company conducts an annual tabletop exercise simulating a ransomware attack. The IR team walks through the response steps, identifying gaps that are then addressed.',
    evidence_tips: [
      'Tabletop exercise records showing date, participants, and scenarios tested',
      'After-action report from the exercise identifying lessons learned',
      'Updated incident response plan reflecting improvements from exercises',
    ],
    common_mistakes: 'The incident response plan is written and filed away but never tested. When a real incident occurs, the plan is discovered to be incomplete and out of date.',
    nist_reference: '3.6.3',
  },
  // ── MAINTENANCE ─────────────────────────────────────────────────────────────
  'MA.L2-3.7.1': {
    plain_english: 'Perform maintenance on your organizational systems in a controlled manner. Maintenance should be planned, approved, and documented.',
    example: 'System maintenance is scheduled during approved maintenance windows. IT submits change tickets for all maintenance activities, which are approved by the IT manager before work begins.',
    evidence_tips: [
      'Maintenance schedule showing planned activities and maintenance windows',
      'Change management tickets for maintenance activities',
      'System maintenance log showing completed activities',
    ],
    common_mistakes: 'System maintenance is performed whenever convenient with no documentation, leaving no record of what was changed.',
    nist_reference: '3.7.1',
  },
  'MA.L2-3.7.2': {
    plain_english: 'Control tools, techniques, mechanisms, and personnel for maintenance. Not everyone should be able to perform system maintenance.',
    example: 'Only approved IT staff and vetted vendors can perform maintenance on systems containing CUI. Remote maintenance by vendors requires IT oversight through the company\'s monitored VPN.',
    evidence_tips: [
      'List of authorized maintenance personnel and vendors',
      'Remote maintenance policy requiring IT supervision',
      'Vendor access logs showing monitored remote sessions',
    ],
    common_mistakes: 'Third-party vendors are given direct, unmonitored remote access to internal systems for maintenance with no IT oversight.',
    nist_reference: '3.7.2',
  },
  'MA.L2-3.7.3': {
    plain_english: 'Sanitize equipment before it leaves your facility for maintenance. Portable media and laptops going to outside vendors could expose CUI if not wiped first.',
    example: 'Before a laptop is sent to a manufacturer for warranty repair, IT backs up the data, wipes the drive using DoD-standard erasure, and reimages the system upon return.',
    evidence_tips: [
      'Equipment sanitization procedure describing how data is removed before external maintenance',
      'Sanitization logs showing drives were wiped before sending equipment out',
      'Return-to-service checklist for equipment returning from external maintenance',
    ],
    common_mistakes: 'A laptop with CUI data is sent to the manufacturer for repair without wiping the drive, exposing data to the vendor\'s technicians.',
    nist_reference: '3.7.3',
  },
  'MA.L2-3.7.4': {
    plain_english: 'Review media containing diagnostic and test programs that are used for maintenance. These tools could be a vector for malware if not controlled.',
    example: 'IT maintains a library of approved bootable USB drives and diagnostic discs. Before each use, the hash of the tool is verified against the vendor\'s published checksum.',
    evidence_tips: [
      'Approved diagnostic tools list with version numbers and hash values',
      'Hash verification records showing tools were verified before use',
      'Storage procedures for maintenance media in a locked cabinet',
    ],
    common_mistakes: 'Technicians download diagnostic tools from random internet sites, potentially introducing malware via the maintenance process itself.',
    nist_reference: '3.7.4',
  },
  'MA.L2-3.7.5': {
    plain_english: 'Require MFA for remote maintenance sessions. Remote maintenance is a common attack vector if not properly secured.',
    example: 'Vendor remote maintenance sessions require both a VPN connection and MFA. IT creates time-limited accounts for vendors that expire after the maintenance window.',
    evidence_tips: [
      'Remote maintenance access policy requiring MFA',
      'VPN configuration showing MFA enforcement for remote connections',
      'Vendor access logs showing MFA was required for remote sessions',
    ],
    common_mistakes: 'Vendors connect via Remote Desktop with only a password, creating an easily brute-forced entry point that persists after maintenance is complete.',
    nist_reference: '3.7.5',
  },
  'MA.L2-3.7.6': {
    plain_english: 'Supervise maintenance activities performed by personnel without access authorization. Outside vendors should not be left alone with your systems.',
    example: 'When the hardware vendor\'s technician arrives to replace a server component, an IT staff member is present throughout the visit.',
    evidence_tips: [
      'Maintenance supervision policy requiring escort for unauthorized personnel',
      'Maintenance activity log showing who was present and what was done',
      'Visitor log entries for vendor maintenance visits',
    ],
    common_mistakes: 'An outside vendor is given an access card and left alone in the server room overnight with no IT staff oversight.',
    nist_reference: '3.7.6',
  },
  // ── MEDIA PROTECTION ────────────────────────────────────────────────────────
  'MP.L2-3.8.1': {
    plain_english: 'Protect digital and non-digital media containing CUI. Paper documents, USB drives, and other media with CUI must be controlled.',
    example: 'CUI printed documents are stored in locked cabinets. CUI on USB drives must be encrypted and stored in a locked area. A media inventory tracks all CUI-containing physical media.',
    evidence_tips: [
      'Media protection policy covering both digital and physical media',
      'Physical media inventory showing CUI-containing media and storage locations',
      'Photos or description of locked storage for physical CUI media',
    ],
    common_mistakes: 'Printed CUI documents are left on desks or in shared printer output trays accessible to anyone who walks by.',
    nist_reference: '3.8.1',
  },
  'MP.L2-3.8.2': {
    plain_english: 'Limit access to CUI on media to authorized individuals. Not everyone in the office should be able to pick up and take a drive with CUI.',
    example: 'External drives containing CUI are kept in a locked cabinet accessible only to cleared personnel. A sign-out log tracks who accesses each piece of media and when.',
    evidence_tips: [
      'Media access control log showing authorized sign-out and return',
      'Physical security controls (locked cabinet) for media storage area',
      'List of personnel authorized to access CUI media',
    ],
    common_mistakes: 'CUI drives are stored in an unlocked drawer in a common area accessible to all employees and visitors.',
    nist_reference: '3.8.2',
  },
  'MP.L2-3.8.3': {
    plain_english: 'Sanitize or destroy media before disposal or reuse. Hard drives and USB drives must be wiped or destroyed before being discarded or reassigned.',
    example: 'Before repurposing or disposing of a workstation, IT performs a secure wipe using NIST SP 800-88 compliant erasure software. Hard drives that cannot be wiped are physically destroyed.',
    evidence_tips: [
      'Media sanitization records showing drives were wiped with compliant tool',
      'Destruction certificates for physically destroyed media',
      'Media sanitization procedure referencing NIST SP 800-88 guidelines',
    ],
    common_mistakes: 'Old computers are donated or sold with the original hard drives still installed, containing all CUI data from the previous user.',
    nist_reference: '3.8.3',
  },
  'MP.L2-3.8.4': {
    plain_english: 'Mark media with necessary CUI markings and distribution limitations so people know it requires protection.',
    example: 'Physical drives and documents containing CUI are labeled with a "CUI" marking including handling instructions: "Handle per CUI policy — do not remove from facility."',
    evidence_tips: [
      'Photos of labeled CUI media and documents',
      'CUI marking policy and labeling standards document',
      'Training records showing employees understand CUI marking requirements',
    ],
    common_mistakes: 'CUI-containing drives and documents are unlabeled, so employees do not know they require special handling or disposal procedures.',
    nist_reference: '3.8.4',
  },
  'MP.L2-3.8.5': {
    plain_english: 'Control access to media containing CUI during transport. Media being carried or shipped outside the facility needs the same protection as inside.',
    example: 'CUI on portable media being transported off-site must be encrypted and transported in a lockable case. Shipping of CUI media requires tracking numbers and signature on delivery.',
    evidence_tips: [
      'Media transport policy with requirements for off-site transport',
      'Encryption verification showing media is encrypted before transport',
      'Shipping records showing tracked, signed delivery for CUI media',
    ],
    common_mistakes: 'An unencrypted USB drive with CUI is casually put in a bag to take home with no tracking or protection if lost.',
    nist_reference: '3.8.5',
  },
  'MP.L2-3.8.6': {
    plain_english: 'Implement cryptographic mechanisms to protect CUI during transport unless protected by physical safeguards.',
    example: 'All laptops and USB drives that may carry CUI off-site use full-disk encryption. Encrypted drives require a passphrase to access even if the physical media is lost.',
    evidence_tips: [
      'BitLocker or VeraCrypt configuration showing encryption is enabled',
      'Policy requiring encryption for all portable media used with CUI',
      'Encryption key management procedure',
    ],
    common_mistakes: 'Staff transport CUI on unencrypted drives relying solely on physical security, which provides no protection if the media is lost or stolen.',
    nist_reference: '3.8.6',
  },
  'MP.L2-3.8.7': {
    plain_english: 'Control the use of removable media on your systems. Limit or eliminate the use of unauthorized removable media to prevent data exfiltration.',
    example: 'USB ports are blocked via Group Policy on all workstations by default. Employees who need removable media for work must request an exception and use company-provided encrypted drives.',
    evidence_tips: [
      'Group Policy configuration showing USB storage restrictions',
      'Removable media policy with approved use cases',
      'Endpoint security configuration showing removable media controls',
    ],
    common_mistakes: 'Employees freely use personal USB drives on work computers with no controls, creating significant data exfiltration and malware introduction risk.',
    nist_reference: '3.8.7',
  },
  'MP.L2-3.8.8': {
    plain_english: 'Prohibit the use of portable storage devices that have no identifiable owner. Unknown devices could be malicious.',
    example: 'If an unknown USB drive is found in the parking lot or lobby, employees are trained to turn it over to IT rather than plugging it in.',
    evidence_tips: [
      'Acceptable Use Policy prohibiting use of unverified or unowned USB drives',
      'Security awareness training records covering USB threat scenarios',
      'Procedure for handling found or suspicious removable media',
    ],
    common_mistakes: 'An employee finds a USB drive in the parking lot and plugs it in to see what is on it, allowing malware to execute automatically.',
    nist_reference: '3.8.8',
  },
  'MP.L2-3.8.9': {
    plain_english: 'Protect and control CUI backups, just like the original CUI. Backup media with CUI is just as sensitive as the live systems.',
    example: 'CUI backups are encrypted using AES-256 before storage. Backup media is stored in a locked, climate-controlled room with access limited to IT staff.',
    evidence_tips: [
      'Backup system configuration showing encryption at rest',
      'Physical security documentation for backup storage location',
      'Backup access logs showing authorized access only',
    ],
    common_mistakes: 'Backups of CUI are stored unencrypted on an external drive in an unlocked storage room, bypassing all access controls on the primary systems.',
    nist_reference: '3.8.9',
  },
  // ── PERSONNEL SECURITY ──────────────────────────────────────────────────────
  'PS.L2-3.9.1': {
    plain_english: 'Screen individuals prior to authorizing access to systems with CUI. Know who you are giving access to before they get it.',
    example: 'All employees who will access CUI systems undergo a background check before their first day. Contract workers undergo the same process before receiving system access.',
    evidence_tips: [
      'Background check policy and the process for verifying results before access is granted',
      'Background check records (or HR attestation) for personnel with CUI access',
      'Onboarding checklist showing access is granted only after screening is complete',
    ],
    common_mistakes: 'New employees are given full system access on their first day before any background check results are received.',
    nist_reference: '3.9.1',
  },
  'PS.L2-3.9.2': {
    plain_english: 'During and after employment, ensure CUI and systems are protected, especially when personnel leave. Termination procedures must include access revocation.',
    example: 'When an employee leaves, a checklist requires IT to disable their accounts within 4 hours, recover company devices, and revoke all access.',
    evidence_tips: [
      'Offboarding checklist showing system access revocation steps',
      'IT ticket records showing accounts were disabled on or before final day',
      'Access review confirming terminated employee accounts were removed',
    ],
    common_mistakes: 'Former employees have active VPN credentials and email accounts for weeks after their last day because there is no formal offboarding process.',
    nist_reference: '3.9.2',
  },
  // ── PHYSICAL PROTECTION ─────────────────────────────────────────────────────
  'PE.L2-3.10.1': {
    plain_english: 'Limit physical access to your organizational systems and the facilities where they are housed. Not everyone should be able to walk into your server room.',
    example: 'The server room is secured with a keycard reader. Only IT staff are authorized for access. Visitor logs document anyone who enters.',
    evidence_tips: [
      'Access control logs for server room and areas with CUI systems',
      'Photos or description of physical access controls (keycard, badge, lock)',
      'Visitor log showing all non-employee access to sensitive areas',
    ],
    common_mistakes: 'The server closet is unlocked and accessible to all office employees and visitors.',
    nist_reference: '3.10.1',
  },
  'PE.L2-3.10.2': {
    plain_english: 'Protect and monitor the physical facility and infrastructure supporting your systems.',
    example: 'The building has physical security including badge access, security cameras at entry points, and an alarm system. Security footage is retained for 90 days.',
    evidence_tips: [
      'Physical security assessment or site security plan',
      'Security camera system documentation showing coverage areas',
      'Alarm system records and security patrol logs',
    ],
    common_mistakes: 'The facility has no cameras or access logging, making it impossible to investigate a physical security breach.',
    nist_reference: '3.10.2',
  },
  'PE.L2-3.10.3': {
    plain_english: 'Escort and monitor visitors in areas where CUI is processed or stored. Visitors should never be left alone.',
    example: 'All visitors to the engineering area must be escorted by an employee at all times. Visitors sign in at reception and wear a visitor badge.',
    evidence_tips: [
      'Visitor escort policy document',
      'Visitor log showing sign-in/out and escort name',
      'Employee training records covering visitor escort procedures',
    ],
    common_mistakes: 'Visitors are given a badge and left to wander the office unescorted, with access to areas where CUI is displayed or processed.',
    nist_reference: '3.10.3',
  },
  'PE.L2-3.10.4': {
    plain_english: 'Maintain audit logs for physical access to sensitive areas. Know who entered, when, and for how long.',
    example: 'The server room keycard system logs all entries with the cardholder name, date, and time. These logs are reviewed monthly and retained for one year.',
    evidence_tips: [
      'Keycard or badge access logs for sensitive areas',
      'Log review records showing physical access logs are reviewed periodically',
      'Retention policy for physical access logs',
    ],
    common_mistakes: 'Physical access is controlled by a combination lock with a shared code and no logging, making it impossible to determine who accessed the area.',
    nist_reference: '3.10.4',
  },
  'PE.L2-3.10.5': {
    plain_english: 'Control and manage physical access devices (keys, keycards, PINs). Lost keys and keycards must be reported and access revoked.',
    example: 'All physical access devices are inventoried. When an employee leaves, their keycard is collected and deactivated. If a keycard is lost, access is immediately revoked.',
    evidence_tips: [
      'Physical access device inventory listing all issued keys and keycards',
      'Offboarding checklist requiring collection and deactivation of access devices',
      'Lost device report and response procedures',
    ],
    common_mistakes: 'Former employees keep their office keys and building badges because no one remembers to collect them during offboarding.',
    nist_reference: '3.10.5',
  },
  'PE.L2-3.10.6': {
    plain_english: 'Enforce safeguards for work at alternate work sites such as home offices. CUI handled at home needs the same protection as in the office.',
    example: 'The remote work policy requires employees working with CUI at home to use a company-managed laptop with VPN, a locked file cabinet for printed CUI, and a private workspace.',
    evidence_tips: [
      'Remote work or telework policy addressing CUI protection requirements',
      'Remote employee security acknowledgment forms',
      'Training records for remote employees on CUI handling at home',
    ],
    common_mistakes: 'Employees work with CUI on personal computers from shared spaces like coffee shops with no company oversight or controls.',
    nist_reference: '3.10.6',
  },
  // ── RISK ASSESSMENT ─────────────────────────────────────────────────────────
  'RA.L2-3.11.1': {
    plain_english: 'Periodically assess risk to your operations, assets, and personnel. A formal risk assessment identifies what could go wrong and how serious it would be.',
    example: 'Once a year, the company conducts a formal risk assessment that identifies threats, vulnerabilities, and the potential impact of various security failures on its defense contracts.',
    evidence_tips: [
      'Risk assessment report dated within the last year',
      'Risk register showing identified risks, likelihood, impact, and mitigation status',
      'Risk assessment methodology document',
    ],
    common_mistakes: 'No formal risk assessment is ever conducted; security decisions are made reactively based on individual incidents.',
    nist_reference: '3.11.1',
  },
  'RA.L2-3.11.2': {
    plain_english: 'Periodically scan for vulnerabilities in your systems and applications. Unpatched systems and software flaws are a primary attack vector.',
    example: 'IT runs a vulnerability scan using a tool like Nessus monthly. Critical and high vulnerabilities are remediated within 30 days. A report is provided to management after each scan.',
    evidence_tips: [
      'Vulnerability scan reports showing scan dates and identified vulnerabilities',
      'Remediation tracking records showing high/critical vulns were patched within SLA',
      'Vulnerability management policy defining scan frequency and remediation timelines',
    ],
    common_mistakes: 'No regular vulnerability scanning is performed. The company only discovers vulnerabilities when they are exploited.',
    nist_reference: '3.11.2',
  },
  'RA.L2-3.11.3': {
    plain_english: 'Remediate vulnerabilities in accordance with your risk assessments. Prioritize what you fix based on risk and track remediation.',
    example: 'Critical vulnerabilities affecting CUI systems are patched within 30 days. High vulnerabilities are patched within 90 days. All remediation is tracked in the ticketing system.',
    evidence_tips: [
      'Vulnerability remediation policy with defined timelines by severity level',
      'Patch management records showing vulnerabilities were remediated per policy',
      'Vulnerability scan comparison showing reduction in open findings over time',
    ],
    common_mistakes: 'Vulnerability scans are run but the results are never acted upon, resulting in the same critical vulnerabilities appearing month after month.',
    nist_reference: '3.11.3',
  },
  // ── SECURITY ASSESSMENT ─────────────────────────────────────────────────────
  'CA.L2-3.12.1': {
    plain_english: 'Periodically assess your security controls to ensure they are effective. Policies on paper mean nothing if the technical controls are not working.',
    example: 'Once a year, the IT security lead reviews each CMMC control, tests whether it is actually implemented, and documents findings. External assessors may also conduct reviews.',
    evidence_tips: [
      'Security control assessment report or CMMC self-assessment records',
      'Assessment methodology and criteria document',
      'Action plan for remediating controls found to be ineffective',
    ],
    common_mistakes: 'Controls are documented in policies but never tested, so deficiencies are only discovered during a C3PAO assessment.',
    nist_reference: '3.12.1',
  },
  'CA.L2-3.12.2': {
    plain_english: 'Develop and implement a plan to address security deficiencies. A Plan of Action and Milestones (POA&M) tracks your progress in fixing problems.',
    example: 'After the annual assessment identifies gaps, a POA&M is created listing each deficiency, the responsible person, milestone dates, and resources needed to remediate.',
    evidence_tips: [
      'POA&M document with current open items and scheduled completion dates',
      'Tracking system showing progress on POA&M items',
      'Management review records showing POA&M is actively managed',
    ],
    common_mistakes: 'Security deficiencies are identified but never formally tracked. The same problems reappear in the next year\'s assessment.',
    nist_reference: '3.12.2',
  },
  'CA.L2-3.12.3': {
    plain_english: 'Monitor security controls on an ongoing basis. Annual assessments are not enough — you need continuous visibility into your security posture.',
    example: 'The SIEM provides continuous monitoring of system events. Quarterly reviews check that all security controls are still functioning as designed.',
    evidence_tips: [
      'Continuous monitoring plan describing what is monitored and how often',
      'SIEM or monitoring system reports showing ongoing visibility',
      'Quarterly control review records',
    ],
    common_mistakes: 'Security controls are only reviewed once a year, leaving months during which control failures go undetected.',
    nist_reference: '3.12.3',
  },
  'CA.L2-3.12.4': {
    plain_english: 'Develop, document, and periodically update a system security plan (SSP) that describes how you protect CUI.',
    example: 'The company maintains an SSP document describing the system boundary, all CUI it handles, applicable security controls, the implementation status of each control, and identified weaknesses.',
    evidence_tips: [
      'System Security Plan (SSP) document with current version and date',
      'SSP review records showing it was updated in the last year',
      'System boundary diagram included in or referenced by the SSP',
    ],
    common_mistakes: 'No SSP exists. The company has individual policies but no integrated document describing the overall security posture.',
    nist_reference: '3.12.4',
  },
  // ── SYSTEM AND COMMUNICATIONS PROTECTION ───────────────────────────────────
  'SC.L2-3.13.1': {
    plain_english: 'Monitor, control, and protect communications at external boundaries and key internal boundaries. Control what crosses your network perimeter.',
    example: 'A firewall controls all traffic entering and leaving the corporate network. An intrusion detection system monitors for suspicious traffic patterns at the boundary.',
    evidence_tips: [
      'Network diagram showing the boundary between your network and external networks',
      'Firewall configuration showing boundary controls',
      'IDS/IPS configuration and alert logs showing boundary monitoring',
    ],
    common_mistakes: 'The network boundary is unclear with no firewall between office WiFi and critical CUI systems.',
    nist_reference: '3.13.1',
  },
  'SC.L2-3.13.2': {
    plain_english: 'Architect your network and systems with security as a design principle. Use segmentation and other architectural controls to limit the impact of breaches.',
    example: 'The network is divided into separate VLANs: one for CUI systems, one for general office use, and one for guest WiFi. Systems in different segments cannot communicate without going through the firewall.',
    evidence_tips: [
      'Network architecture diagram showing segmentation (VLANs, DMZ)',
      'Firewall inter-VLAN routing rules',
      'Security architecture documentation describing design decisions',
    ],
    common_mistakes: 'All systems from the CEO\'s laptop to the CUI server to the office printer are on the same flat network with no segmentation.',
    nist_reference: '3.13.2',
  },
  'SC.L2-3.13.3': {
    plain_english: 'Separate user functionality from system management functionality. Administrative interfaces should be separate from regular user-facing systems.',
    example: 'Server administration is done only through a dedicated management VLAN accessible only to IT staff. Admin interfaces are not accessible from the user network.',
    evidence_tips: [
      'Network diagram showing separate management network or VLAN',
      'Access control lists showing user network cannot reach management interfaces',
      'Policy requiring out-of-band management where possible',
    ],
    common_mistakes: 'Web-based server administration interfaces are accessible from the regular user network using default credentials.',
    nist_reference: '3.13.3',
  },
  'SC.L2-3.13.4': {
    plain_english: 'Prevent unauthorized information transfer through shared system resources. When multiple users share resources, one should not be able to read another\'s data.',
    example: 'Shared storage systems use file system permissions to ensure users can only see their own data. Memory is properly initialized before allocation to new processes.',
    evidence_tips: [
      'Operating system and virtualization configuration confirming resource isolation',
      'File share permissions showing proper data isolation between users',
      'Application architecture review confirming shared resource access controls',
    ],
    common_mistakes: 'Multi-tenant systems have misconfigured permissions allowing one tenant to browse another\'s CUI files.',
    nist_reference: '3.13.4',
  },
  'SC.L2-3.13.5': {
    plain_english: 'Implement subnetworks for publicly accessible system components. Web-facing systems should be in a DMZ, separate from systems containing CUI.',
    example: 'The public-facing company website is hosted in a DMZ network segment. Direct connections from the DMZ to the internal CUI systems are blocked by the firewall.',
    evidence_tips: [
      'Network diagram showing DMZ architecture with public-facing systems separated',
      'Firewall rules showing DMZ to internal network traffic is restricted',
      'Penetration test results verifying DMZ isolation',
    ],
    common_mistakes: 'The company website server is on the same internal network segment as the file server containing CUI, so compromising the website provides direct access to CUI.',
    nist_reference: '3.13.5',
  },
  'SC.L2-3.13.6': {
    plain_english: 'Deny network communications by default. Traffic should be blocked unless it is explicitly allowed.',
    example: 'All firewall policies are deny-all by default. Only specific services (HTTPS outbound, SMTP, VPN) are explicitly allowed. New connections require a change request.',
    evidence_tips: [
      'Firewall rule set showing default-deny policy with specific allow rules',
      'Network security policy document stating deny-by-default approach',
      'Firewall change management records',
    ],
    common_mistakes: 'Firewalls have broad allow-any rules that permit all outbound traffic, enabling malware to call home and exfiltrate data freely.',
    nist_reference: '3.13.6',
  },
  'SC.L2-3.13.7': {
    plain_english: 'Prevent split tunneling when users are connected to the corporate network remotely. All traffic from remote users should go through your security controls.',
    example: 'VPN is configured in full-tunnel mode. When an employee is connected to the VPN, all internet traffic goes through the company\'s firewall and content filter.',
    evidence_tips: [
      'VPN configuration showing full-tunnel mode is enforced',
      'VPN policy document prohibiting split tunneling',
      'Network traffic analysis confirming all traffic routes through the corporate gateway',
    ],
    common_mistakes: 'The VPN uses split tunneling, allowing employees to access CUI systems through the VPN while simultaneously browsing the internet directly, bypassing all corporate security controls.',
    nist_reference: '3.13.7',
  },
  'SC.L2-3.13.8': {
    plain_english: 'Implement cryptographic mechanisms to prevent unauthorized disclosure of CUI during transmission. Encrypt CUI when it is sent across networks.',
    example: 'All internal communication involving CUI uses TLS 1.2 or higher. Email with CUI is sent via encrypted email (S/MIME) or through a secure file transfer portal.',
    evidence_tips: [
      'Network configuration showing TLS is enforced for all CUI communications',
      'Email encryption policy and configuration',
      'SSL/TLS certificate inventory for all systems transmitting CUI',
    ],
    common_mistakes: 'CUI is sent via plain email attachments with no encryption, visible to anyone who intercepts the network traffic.',
    nist_reference: '3.13.8',
  },
  'SC.L2-3.13.9': {
    plain_english: 'Terminate network connections after a defined inactivity period or when sessions complete. Do not leave network connections open indefinitely.',
    example: 'VPN sessions automatically disconnect after 8 hours of inactivity. Applications close database connections when no longer needed.',
    evidence_tips: [
      'VPN session timeout configuration',
      'Application configuration showing connection timeouts and session management',
      'Network device configuration showing connection state management',
    ],
    common_mistakes: 'VPN connections remain open indefinitely even when the user\'s laptop is closed, creating long-lived network sessions that could be hijacked.',
    nist_reference: '3.13.9',
  },
  'SC.L2-3.13.10': {
    plain_english: 'Establish and manage cryptographic keys for any cryptography used to protect CUI. Good encryption can be undone by poor key management.',
    example: 'Encryption keys for BitLocker are backed up to Active Directory and accessible only to IT administrators. Keys are stored separately from the data they encrypt.',
    evidence_tips: [
      'Key management policy or procedure document',
      'BitLocker key backup configuration showing keys stored in Active Directory',
      'Key escrow or recovery procedure documentation',
    ],
    common_mistakes: 'BitLocker recovery keys are stored in a text file on the same laptop that is encrypted, making them useless for recovery if the main drive fails.',
    nist_reference: '3.13.10',
  },
  'SC.L2-3.13.11': {
    plain_english: 'Employ FIPS-validated cryptography when used to protect the confidentiality of CUI. Not all encryption is created equal — use NIST-approved, validated implementations.',
    example: 'The company uses Windows BitLocker in FIPS-compliant mode. VPN connections use AES-256 with FIPS-validated cryptographic modules. Older algorithms like DES and RC4 are prohibited.',
    evidence_tips: [
      'FIPS 140-2/140-3 compliant configuration settings for encryption tools',
      'Software and hardware certificates showing FIPS validation',
      'Policy prohibiting non-FIPS cryptographic algorithms for CUI protection',
    ],
    common_mistakes: 'The company uses home-grown encryption or self-signed certificates with outdated algorithms that have not been validated by NIST.',
    nist_reference: '3.13.11',
  },
  'SC.L2-3.13.12': {
    plain_english: 'Prohibit remote activation of collaborative computing devices like cameras and microphones. These devices should only be active when users intentionally enable them.',
    example: 'Company laptops have physical camera covers. Policy prohibits the installation of software that can remotely activate cameras. Conferencing tools show a visible indicator when active.',
    evidence_tips: [
      'Policy prohibiting unauthorized remote activation of cameras and microphones',
      'Physical camera cover inventory for sensitive area workstations',
      'Configuration settings preventing unauthorized remote access to A/V devices',
    ],
    common_mistakes: 'Employees in sensitive areas use laptops with uncovered cameras and no policy on microphone use, risking inadvertent capture of sensitive discussions.',
    nist_reference: '3.13.12',
  },
  'SC.L2-3.13.13': {
    plain_english: 'Control and monitor the use of mobile code. Mobile code (like JavaScript) that runs automatically can be a security risk.',
    example: 'Web browsers are configured to block Java and disable unsigned ActiveX controls. Group Policy restricts browser extensions to an approved list.',
    evidence_tips: [
      'Browser security policy and configuration settings',
      'Group Policy configuration controlling mobile code execution',
      'Email client security configuration blocking active content',
    ],
    common_mistakes: 'Browsers allow all mobile code to run without restriction from untrusted sites, enabling drive-by malware downloads.',
    nist_reference: '3.13.13',
  },
  'SC.L2-3.13.14': {
    plain_english: 'Control and monitor VoIP communications. Voice over IP systems can be intercepted or used to exfiltrate information if not secured.',
    example: 'VoIP communications on networks containing CUI are encrypted using SRTP. VoIP systems are on a separate VLAN from CUI data systems. Call records are retained.',
    evidence_tips: [
      'VoIP system configuration showing encryption (SRTP) is enabled',
      'Network diagram showing VoIP VLAN separation',
      'VoIP usage policy covering acceptable use for sensitive discussions',
    ],
    common_mistakes: 'VoIP systems use unencrypted SIP/RTP protocols making voice traffic interceptable on the local network.',
    nist_reference: '3.13.14',
  },
  'SC.L2-3.13.15': {
    plain_english: 'Protect the authenticity of communications sessions. Sessions must be protected against man-in-the-middle attacks and session hijacking.',
    example: 'All web applications require HTTPS with valid certificates. TLS session resumption uses session IDs with short expiration times. Applications use CSRF tokens.',
    evidence_tips: [
      'SSL/TLS certificate configuration showing valid, trusted certificates',
      'Web application security configuration showing session protection controls',
      'TLS configuration scan results (e.g., SSL Labs test showing A rating)',
    ],
    common_mistakes: 'Internal web applications use self-signed certificates that browsers warn about, training users to ignore certificate errors.',
    nist_reference: '3.13.15',
  },
  'SC.L2-3.13.16': {
    plain_english: 'Protect CUI at rest. Data stored on systems must be protected from unauthorized access, even if the physical media is stolen.',
    example: 'All servers containing CUI use full-disk encryption. Database backups are encrypted before storage. CUI is not stored on unencrypted removable media.',
    evidence_tips: [
      'Server disk encryption configuration showing encryption is enabled',
      'Database encryption configuration or at-rest encryption documentation',
      'Storage policy prohibiting unencrypted storage of CUI',
    ],
    common_mistakes: 'CUI databases and file shares are not encrypted at rest, so anyone who gains physical access to the storage media can read all data without credentials.',
    nist_reference: '3.13.16',
  },
  // ── SYSTEM AND INFORMATION INTEGRITY ────────────────────────────────────────
  'SI.L2-3.14.1': {
    plain_english: 'Identify, report, and correct information and system flaws in a timely manner. Patch your systems and fix security vulnerabilities before attackers exploit them.',
    example: 'Critical patches are applied to all systems within 30 days of release. Patches are tested in a development environment first. A patch tracking log records the status of all outstanding patches.',
    evidence_tips: [
      'Patch management policy with defined patching timelines by severity',
      'Patch compliance report showing percentage of systems are current',
      'Vulnerability scan before and after patching showing vulnerabilities were remediated',
    ],
    common_mistakes: 'Systems are not patched because "we do not want to break anything," leaving known exploitable vulnerabilities in production for months.',
    nist_reference: '3.14.1',
  },
  'SI.L2-3.14.2': {
    plain_english: 'Deploy antivirus/anti-malware tools on all systems and keep them updated. Malware protection must have current signatures to detect the latest threats.',
    example: 'All workstations and servers run an endpoint protection solution with automatic signature updates every 4 hours. Full system scans are scheduled weekly.',
    evidence_tips: [
      'Endpoint protection management console showing coverage and update status',
      'Anti-malware policy specifying required tools and update frequency',
      'Scan reports showing regular full scans were completed',
    ],
    common_mistakes: 'Antivirus is installed on workstations but not on servers, or signatures are months out of date because auto-update was never configured.',
    nist_reference: '3.14.2',
  },
  'SI.L2-3.14.3': {
    plain_english: 'Monitor your systems and communications for attacks and indicators of attack. Passive tools that only log are not enough — you need active monitoring.',
    example: 'An IDS is deployed at the network perimeter and monitors for known attack patterns. SIEM alerts fire when abnormal behaviors are detected.',
    evidence_tips: [
      'IDS/IPS configuration and alert logs',
      'SIEM detection rules showing what attack indicators are monitored',
      'Security monitoring report showing active monitoring is in place',
    ],
    common_mistakes: 'Logs are collected but no alerting is configured for attack indicators, making intrusions detectable only by manually reading logs after the fact.',
    nist_reference: '3.14.3',
  },
  'SI.L2-3.14.4': {
    plain_english: 'Identify and block unauthorized use of your systems. Detection is only valuable if it leads to response.',
    example: 'When the IDS detects a port scan originating from an internal device, the SIEM creates an alert. IT investigates and blocks the source IP at the firewall if malicious activity is confirmed.',
    evidence_tips: [
      'Incident response procedure for responding to detected unauthorized use',
      'Alert tickets showing detected unauthorized activity was investigated and remediated',
      'Block list or firewall rules updated based on detected threats',
    ],
    common_mistakes: 'Intrusion detection alerts fire and are ignored because no one is responsible for reviewing them, rendering the detection system useless.',
    nist_reference: '3.14.4',
  },
  'SI.L2-3.14.5': {
    plain_english: 'Perform periodic scans of your systems and real-time scans of files from external sources. Incoming files, especially email attachments, are a primary malware delivery vector.',
    example: 'Email security filters scan all attachments for malware before delivery. Downloads from the internet are scanned in real time. Weekly scheduled malware scans run on all systems overnight.',
    evidence_tips: [
      'Email security configuration showing attachment scanning is enabled',
      'Endpoint protection configuration showing real-time and scheduled scans',
      'Scan logs showing periodic scans complete successfully',
    ],
    common_mistakes: 'Real-time malware scanning is disabled because it slows down the computer, relying only on weekly scans that miss actively executing malware.',
    nist_reference: '3.14.5',
  },
  'SI.L2-3.14.6': {
    plain_english: 'Monitor your systems to detect attacks and potential indicators of compromise. Go beyond perimeter monitoring to monitor systems from within.',
    example: 'Endpoint Detection and Response (EDR) software monitors all workstations and servers for suspicious process behavior, file modifications, and network connections.',
    evidence_tips: [
      'EDR solution deployment and configuration documentation',
      'Host-based IDS configuration and alert logs',
      'Security monitoring dashboard showing active coverage across all endpoints',
    ],
    common_mistakes: 'Monitoring is only at the network perimeter — attackers who gain internal access can move laterally and exfiltrate data with no host-level detection.',
    nist_reference: '3.14.6',
  },
  'SI.L2-3.14.7': {
    plain_english: 'Identify unauthorized use of organizational systems. You must be able to detect when someone is using your systems in ways they should not be.',
    example: 'A user behavior analytics tool detects when an employee\'s account starts accessing an unusual volume of files late at night — a potential indicator of data exfiltration. An alert is generated for review.',
    evidence_tips: [
      'User and entity behavior analytics (UEBA) configuration and alerts',
      'Baseline behavior documentation and anomaly detection rules',
      'Security alert records showing unauthorized use was detected and investigated',
    ],
    common_mistakes: 'No baseline for normal user behavior is established, making it impossible to distinguish legitimate unusual activity from an insider threat or compromised account.',
    nist_reference: '3.14.7',
  },
};

export default CONTROL_EXPLANATIONS;
